import { useCreateReport } from '@/hooks/api/reports/useCreateReport';
import { MetadataFilterValue, ReportType } from '@/api/generatedApi';
import { FilterDataPopover } from '@/components/Search/SearchControls/SearchFiltersSection/FilterDataPopover/FilterDataPopover';
import { useTextSearch } from '@/hooks/api/crossTenant/textsearch/useTextSearch';
import { useGetFolders } from '@/hooks/useGetFolders';
import { buildFolderTree, flattenFolderTree } from '@/utils/buildFolderTree';
import { normalizeFolderPath } from '@/components/Search/utils';
import { usePreserveDataDuringLoading } from '@/components/FilterData/hooks/usePreserveDataDuringLoading';
import { InputBox } from '@/components/Inputs/InputBox';
import { TextInput } from '@/components/Inputs/TextInput';
import { FilterSectionHeader } from '@/components/Search/SearchControls/SearchFiltersSection/FilterSectionHeader';
import { MetadataFilter } from '@/components/Search/SearchControls/SearchFiltersSection/MetadataFilter';
import { MatchRangeSlider } from '@/components/MatchRangeSlider';
import { VALID_3D_EXTENSIONS } from '@physna/supported-files';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
} from '@mui/material';
import { useState } from 'react';
import { REPORT_DESCRIPTIONS, REPORT_TITLES } from './consts';

const normalizeReportFolderPath = (folderPath: string): string => {
  let normalized = normalizeFolderPath(folderPath);
  if (!normalized.startsWith('/')) {
    normalized = `/${normalized}`;
  }
  return normalized;
};

const normalizeExtension = (extension: string): string => {
  return extension.startsWith('.') ? extension : `.${extension}`;
};

const getInitialThresholds = (reportType: ReportType): { min: number; max: number } => {
  if (reportType === ReportType.DUPLICATION) return { min: 100, max: 100 };
  if (reportType === ReportType.SIMPLIFICATION) return { min: 95, max: 100 };
  return { min: 80, max: 100 };
};

const getDefaultReportName = (reportType: ReportType): string => {
  const reportTitle = REPORT_TITLES[reportType].replace(' Report', '');
  const date = new Date().toLocaleDateString();
  return `${reportTitle} ${date}`;
};

const tryParseJsonArray = (value: string): string[] | undefined => {
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
};

const toggleStringInArray = (array: string[], value: string): string[] => {
  return array.includes(value) ? array.filter(item => item !== value) : [...array, value];
};

export const CreateReportModal = ({
  isOpen,
  onClose,
  reportType,
  onSuccess,
}: {
  isOpen: boolean;
  onClose: () => void;
  reportType: ReportType;
  onSuccess?: (reportId: string) => void;
}) => {
  const { createReport, isCreating } = useCreateReport();
  const { folders } = useGetFolders();
  const folderTree = buildFolderTree(folders);
  const foldersAsFilterData = flattenFolderTree(folderTree);

  const initialThresholds = getInitialThresholds(reportType);
  const [name, setName] = useState('');
  const [minThreshold, setMinThreshold] = useState(initialThresholds.min);
  const [maxThreshold, setMaxThreshold] = useState(initialThresholds.max);
  const [selectedFolders, setSelectedFolders] = useState<string[]>([]);
  const [selectedMetadata, setSelectedMetadata] = useState<Record<string, MetadataFilterValue>>({});
  const [selectedExtensions, setSelectedExtensions] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');

  const hasSelectedMetadataFilters = Object.keys(selectedMetadata).length > 0;
  const textSearchEnabled =
    selectedFolders.length > 0 ||
    Object.keys(selectedMetadata).length > 0 ||
    searchQuery.length > 0 ||
    selectedExtensions.length > 0;

  const { data, isFetching: isFetchingFilters } = useTextSearch({
    searchQuery: searchQuery ?? '',
    selectedFilters: {
      extensions: selectedExtensions,
      folders: selectedFolders,
      metadata: selectedMetadata,
    },
    enabled: textSearchEnabled,
  });

  const searchData = usePreserveDataDuringLoading(data, isFetchingFilters);

  const handleClearAllFolderFilters = () => {
    setSelectedFolders([]);
  };

  const handleClearAllExtensions = () => {
    setSelectedExtensions([]);
  };

  const handleClearAllMetadataFilters = () => {
    setSelectedMetadata({});
  };

  const handleFilterChange = (filterName: string, filterValue: string | number | boolean | undefined) => {
    if (filterName === 'Folders') {
      if (filterValue === undefined) {
        handleClearAllFolderFilters();
        return;
      }
      const valueString = String(filterValue);
      const parsedArray = tryParseJsonArray(valueString);
      setSelectedFolders(previous => parsedArray ?? toggleStringInArray(previous, valueString));
      return;
    }

    if (filterName === 'Extensions') {
      if (filterValue === undefined) {
        handleClearAllExtensions();
        return;
      }
      const valueString = String(filterValue);
      const parsedArray = tryParseJsonArray(valueString);
      setSelectedExtensions(previous => parsedArray ?? toggleStringInArray(previous, valueString));
      return;
    }

    const metadataFilterData = searchData?.filterData?.metadata?.[filterName] || [];
    const valueString = typeof filterValue === 'object' ? JSON.stringify(filterValue) : String(filterValue);
    const originalFilterData = metadataFilterData.find(
      item => (typeof item.filter === 'object' ? JSON.stringify(item.filter) : String(item.filter)) === valueString
    );
    const typedValue = originalFilterData ? originalFilterData.filter : filterValue;

    setSelectedMetadata(previous => {
      const currentValue = previous[filterName];
      const currentValueString = typeof currentValue === 'object' ? JSON.stringify(currentValue) : String(currentValue);

      if (typedValue === undefined || (currentValue !== undefined && currentValueString === valueString)) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [filterName]: _removed, ...rest } = previous;
        return rest;
      }

      return { ...previous, [filterName]: typedValue };
    });
  };

  const handleSubmit = () => {
    createReport(
      {
        type: reportType,
        name: name.trim() || getDefaultReportName(reportType),
        minThreshold,
        maxThreshold,
        searchQuery: searchQuery || undefined,
        metadataFilters: Object.keys(selectedMetadata).length > 0 ? selectedMetadata : undefined,
        folderPaths: selectedFolders.length > 0 ? selectedFolders.map(f => normalizeReportFolderPath(f)) : undefined,
        extensions:
          selectedExtensions.length > 0
            ? selectedExtensions.map(extension => normalizeExtension(extension))
            : undefined,
      },
      {
        onSuccess: response => {
          handleClose();
          onSuccess?.(response.data.report.id);
        },
      }
    );
  };

  const handleClose = () => {
    const resetThresholds = getInitialThresholds(reportType);
    setName('');
    setMinThreshold(resetThresholds.min);
    setMaxThreshold(resetThresholds.max);
    setSelectedFolders([]);
    setSelectedMetadata({});
    setSelectedExtensions([]);
    setSearchQuery('');
    onClose();
  };

  const hasMetadata = Object.keys(searchData?.filterData?.metadata || {}).length > 0;

  return (
    <Dialog
      fullWidth
      maxWidth='sm'
      open={isOpen}
      onClose={handleClose}
      aria-modal='true'
      slotProps={{
        paper: {
          sx: {
            borderRadius: '12px',
          },
        },
      }}
    >
      <DialogTitle>{`Create ${REPORT_TITLES[reportType]}`}</DialogTitle>
      <DialogContent>
        <TextField
          label='Report Name'
          value={name}
          onChange={event => setName(event.target.value)}
          placeholder={getDefaultReportName(reportType)}
          fullWidth
          margin='normal'
          helperText={`${REPORT_DESCRIPTIONS[reportType]}`}
        />
        {reportType === ReportType.CUSTOM && (
          <>
            <FilterSectionHeader title='Common Geometry' hasSelectedFilters={false} onClearAll={() => {}} />
            <InputBox>
              <Box marginTop='8px'>
                <MatchRangeSlider
                  minThreshold={minThreshold}
                  maxThreshold={maxThreshold}
                  onMinThresholdChange={setMinThreshold}
                  onMaxThresholdChange={setMaxThreshold}
                />
              </Box>
            </InputBox>
          </>
        )}
        {foldersAsFilterData.length > 0 && (
          <>
            <FilterSectionHeader
              title='Folders'
              hasSelectedFilters={selectedFolders.length > 0}
              onClearAll={handleClearAllFolderFilters}
            />
            <InputBox>
              <FilterDataPopover
                filterName='Folders'
                filterData={foldersAsFilterData}
                enableMultiSelect
                isDeferredApply
                useJsonPayload
                recursive
                onFilterChange={(_checkedCount, value) => handleFilterChange('Folders', value)}
                selectedFilterValues={selectedFolders}
              />
            </InputBox>
          </>
        )}
        <FilterSectionHeader title='Text' hasSelectedFilters={false} onClearAll={() => {}} />
        <TextInput placeholder='All' value={searchQuery} onChange={event => setSearchQuery(event.target.value)} />
        <FilterSectionHeader
          title='Extensions'
          hasSelectedFilters={selectedExtensions.length > 0}
          onClearAll={handleClearAllExtensions}
        />
        <InputBox>
          <FilterDataPopover
            filterName='Extensions'
            filterData={VALID_3D_EXTENSIONS.map(extension => extension.slice(1))
              .sort((a, b) => a.localeCompare(b))
              .map(extension => ({
                filter: extension,
                label: extension,
                value: extension,
                count:
                  searchData?.filterData?.extensions?.find(
                    extensionDataPoint => extensionDataPoint.filter === extension
                  )?.count ?? 0,
              }))}
            enableMultiSelect
            isDeferredApply
            useJsonPayload
            hideZeroCounts
            onFilterChange={(_checkedCount, value) => handleFilterChange('Extensions', value)}
            selectedFilterValues={selectedExtensions}
          />
        </InputBox>
        <FilterSectionHeader
          title='Metadata'
          hasSelectedFilters={hasSelectedMetadataFilters}
          onClearAll={handleClearAllMetadataFilters}
        />
        {hasMetadata ? (
          Object.entries(searchData?.filterData.metadata || {})
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([filterName, filterDataPointMetadataValue], index) => (
              <Box key={`${filterName}-${index}`} data-testid={`metadata-filter-${index}`}>
                <InputBox>
                  <MetadataFilter
                    filterName={filterName}
                    filterData={filterDataPointMetadataValue}
                    selectedFilters={{
                      folders: selectedFolders,
                      extensions: selectedExtensions,
                      metadata: selectedMetadata,
                    }}
                    onFilterChange={handleFilterChange}
                  />
                </InputBox>
                {index < Object.entries(searchData?.filterData.metadata || {}).length - 1 && (
                  <Box sx={{ marginTop: '4px', padding: '0 8px 0 8px' }}>
                    <Divider />
                  </Box>
                )}
              </Box>
            ))
        ) : (
          <InputBox empty disabled />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isCreating}>
          Cancel
        </Button>
        <Button variant='contained' onClick={handleSubmit} disabled={isCreating} sx={{ width: '100px' }}>
          {isCreating ? <CircularProgress size={20} /> : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
